import {
  DIN_ADDRESS_MAX,
  DIN_ADDRESS_MIN,
  DIN_CHANNEL_MAX,
  DIN_CHANNEL_NUMBERING,
  DIN_VALUE,
  RCU_CMDTYPE,
  RCU_GROUP,
  RCU_TOP_TYPE,
  dintypeHex,
} from '../../catalog/protocol';
import type { DinModuleId } from '../../catalog/protocol';
import type { ConfigIssue } from '../../catalog/issue';
import type { Result } from '../../result';
import type { RcuDocument } from '../../wire';
import { fieldRange, validated } from './validate';

/**
 * DIN modules: an address is 0–15, and a channel counts from one up to 15.
 *
 * The console numbers channels from 1 the way the module is labelled, except
 * here: these builders take the number that goes on the wire, and the panel
 * converts from what the technician sees. Keeping the conversion at the edge
 * means a payload the console shows is exactly the payload it sends.
 */

export interface DinModuleRefInput {
  readonly group: number;
  readonly module: DinModuleId;
  readonly addr: number;
}

export interface DinChannelInput extends DinModuleRefInput {
  readonly channel: number;
}

export interface OperateDinInput extends DinChannelInput {
  /** The module's own scale: 0–100, or `DIN_VALUE.DECREASE`/`INCREASE` to jog. */
  readonly value: number;
}

const moduleChecks = (input: DinModuleRefInput): readonly (ConfigIssue | null)[] => [
  fieldRange('Group', input.group, RCU_GROUP.MIN, RCU_GROUP.MAX),
  fieldRange('Address', input.addr, DIN_ADDRESS_MIN, DIN_ADDRESS_MAX),
];

const channelChecks = (input: DinChannelInput): readonly (ConfigIssue | null)[] => [
  ...moduleChecks(input),
  fieldRange('Channel', input.channel, DIN_CHANNEL_NUMBERING.FIRMWARE_FIRST, DIN_CHANNEL_MAX),
];

export const operateDin = (input: OperateDinInput): Result<RcuDocument> =>
  validated(
    {
      type: RCU_TOP_TYPE.COMMAND,
      data: {
        cmdtype: RCU_CMDTYPE.OPERATE_DIN,
        groupid: input.group,
        dintype: dintypeHex(input.module),
        addr: input.addr,
        channel: input.channel,
        value: input.value,
      },
    },
    [
      ...channelChecks(input),
      fieldRange('Value', input.value, DIN_VALUE.MIN, DIN_VALUE.INCREASE),
    ],
  );

/** No submit ack: the state arrives later as a `dinstatus` event. */
export const readDin = (input: DinChannelInput): Result<RcuDocument> =>
  validated(
    {
      type: RCU_TOP_TYPE.COMMAND,
      data: {
        cmdtype: RCU_CMDTYPE.READ_DIN,
        groupid: input.group,
        dintype: dintypeHex(input.module),
        addr: input.addr,
        channel: input.channel,
      },
    },
    channelChecks(input),
  );

/** Reads every channel of one module; the reply is a `dinstatusall`. */
export const readAllDin = (input: DinModuleRefInput): Result<RcuDocument> =>
  validated(
    {
      type: RCU_TOP_TYPE.COMMAND,
      data: {
        cmdtype: RCU_CMDTYPE.READ_ALL_DIN,
        groupid: input.group,
        dintype: dintypeHex(input.module),
        addr: input.addr,
      },
    },
    moduleChecks(input),
  );
